const TSE_RESULTS_PAGE = 'https://resultados.tse.jus.br/oficial/app/index.html';
const TSE_CANDIDATES_PAGE = (year: string) => `https://dadosabertos.tse.jus.br/dataset/candidatos-${year}`;
const CAMARA_API_DOCS = 'https://dadosabertos.camara.leg.br/swagger/api.html';
const SENADO_DATA_CATALOG = 'https://www12.senado.leg.br/dados-abertos/dados-abertos';
const SENADO_PLENARY_DATA = 'https://www12.senado.leg.br/dados-abertos/legislativo/plenario';
const CAMARA_SPEECHES_PAGE = 'https://www2.camara.leg.br/atividade-legislativa/discursos-e-notas-taquigraficas';
const CAMARA_VOTES_PAGE = 'https://www2.camara.leg.br/atividade-legislativa/plenario/resultadoVotacao';
const TRANSFEREGOV_SPECIAL_TRANSFERS_PAGE = 'https://qlik-publico.paineis.gov.br/extensions/parlamentar/parlamentar.html';
const SIOP_PUBLIC_BUDGET_PAGE = 'https://www.gov.br/planejamento/pt-br/assuntos/orcamento';
const SIOP_OPEN_DATA_PAGE = 'https://orcamento.dados.gov.br/siopdoc/doku.php/acesso_publico:dados_abertos';
const SIOP_AMENDMENTS_PANEL = 'https://www1.siop.planejamento.gov.br/QvAJAXZfc/opendoc.htm?anonymous=true&document=IAS%2FExecucao_Orcamentaria.qvw&host=QVS%40pqlk04&sheet=SH05';

function camaraPublicPage(url: URL): string {
  const path = url.pathname;
  if (/\/SitCamaraWS\/|\.asmx(?:\/|$)/i.test(path)) return CAMARA_SPEECHES_PAGE;
  if (/\/arquivos\/votacoesVotos(?:\/|$)/i.test(path)) return CAMARA_VOTES_PAGE;
  const proposal = path.match(/\/proposicoes\/(\d+)(?:\/|$)/);
  if (proposal) return `https://www.camara.leg.br/proposicoesWeb/fichadetramitacao?idProposicao=${proposal[1]}`;

  const deputy = path.match(/\/deputados\/(\d+)(?:\/|$)/);
  if (deputy) {
    if (/\/despesas(?:\/|$)/.test(path)) return 'https://www.camara.leg.br/transparencia/gastos-parlamentares';
    return `https://www.camara.leg.br/deputados/${deputy[1]}`;
  }

  if (/\/votacoes(?:\/|$)/.test(path)) return CAMARA_VOTES_PAGE;
  if (/\/partidos(?:\/|$)/.test(path)) return 'https://www.camara.leg.br/deputados/liderancas-e-bancadas-partidarias/contatos';
  return CAMARA_API_DOCS;
}

function senadoPublicPage(url: URL): string {
  const path = url.pathname;
  const senator = path.match(/\/senador\/(\d+)(?:\/|\.|$)/);
  if (senator) return `https://www25.senado.leg.br/web/senadores/senador/-/perfil/${senator[1]}`;
  if (/\/senador\/lista\//.test(path)) return 'https://www25.senado.leg.br/web/senadores/em-exercicio';
  if (/\/plenario(?:\/|$)|\/votacao(?:\/|$)/.test(path)) return SENADO_PLENARY_DATA;
  if (/\/materia(?:s|\/|$)|\/proposicao(?:\/|$)/.test(path)) return 'https://www25.senado.leg.br/web/atividade/materias';
  return SENADO_DATA_CATALOG;
}

/**
 * Converts machine endpoints and raw data downloads into the source's public,
 * human-readable consultation page. The original endpoint remains in the API
 * and database as provenance; only outbound links shown to site visitors use
 * this presentation URL.
 */
export function publicSourcePage(href: string | null | undefined): string | null {
  if (!href) return null;
  let url: URL;
  try { url = new URL(href); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const path = url.pathname;

  if (host === 'resultados.tse.jus.br') return TSE_RESULTS_PAGE;
  if (host === 'cdn.tse.jus.br') {
    const year = path.match(/(?:consulta_cand|bem_candidato)_(\d{4})/i)?.[1];
    return year ? TSE_CANDIDATES_PAGE(year) : 'https://dadosabertos.tse.jus.br/dataset';
  }
  if (host === 'dadosabertos.tse.jus.br') {
    const dataset = path.match(/\/dataset\/([^/]+)/)?.[1];
    if (dataset) return `https://dadosabertos.tse.jus.br/dataset/${dataset}`;
    if (/\.(?:json|xml|csv|zip)$/i.test(path)) return 'https://dadosabertos.tse.jus.br/dataset';
  }

  if (host === 'dadosabertos.camara.leg.br' || host.endsWith('.camara.leg.br') && /\/api\//i.test(path)) return camaraPublicPage(url);
  if (host.endsWith('senado.leg.br') && (/\/dadosabertos(?:\/|$)/i.test(path) || /\/api\//i.test(path))) return senadoPublicPage(url);
  if (host === 'adm.senado.gov.br') return 'https://www12.senado.leg.br/dados-abertos/administrativo/senadores';

  if (host === 'orcamento.dados.gov.br' && /\/sparql(?:\/|$)/i.test(path)) return SIOP_OPEN_DATA_PAGE;
  if (/siop/i.test(host)) {
    if (/\/sparql(?:\/|$)/i.test(path)) return SIOP_OPEN_DATA_PAGE;
    if (/\/modulo\/impositivo(?:\/|$)/i.test(path)) return SIOP_AMENDMENTS_PANEL;
    if (/\/services(?:\/|$)/i.test(path) || url.searchParams.has('wsdl') || /\.wsdl$/i.test(path)) return SIOP_PUBLIC_BUDGET_PAGE;
  }

  if (host === 'servicodados.ibge.gov.br') {
    if (/\/malhas(?:\/|$)/i.test(path)) return 'https://www.ibge.gov.br/geociencias/organizacao-do-territorio/malhas-territoriais/15774-malhas.html';
    return 'https://www.ibge.gov.br/cidades-e-estados.html?lang=pt-BR';
  }
  if (host === 'api.portaldatransparencia.gov.br' || host === 'portaldatransparencia.gov.br' && /\/api\//i.test(path)) return 'https://portaldatransparencia.gov.br/emendas/consulta';
  if (/transferegov/i.test(host) && (/^api(?:[-.]|$)/i.test(host) || /\/especiais(?:\/|$)/i.test(path))) return TRANSFEREGOV_SPECIAL_TRANSFERS_PAGE;

  // The old Câmara SOAP service returns XML even though its URL has no .xml suffix.
  if (/\/SitCamaraWS\/|\.asmx(?:\/|$)/i.test(path)) return CAMARA_SPEECHES_PAGE;

  const rawFormat = ['format', 'output', 'type'].some((key) => /^(?:json|xml)$/i.test(url.searchParams.get(key) ?? ''));
  const rawFile = /(?:^|\/)[^/]+\.(?:json|xml|csv|zip|asmx|wsdl)(?:\/|$)/i.test(path) || /@@download\/file$/i.test(path) || url.searchParams.has('wsdl') || /\/services(?:\/|$)/i.test(path) || rawFormat;
  const apiEndpoint = /\/api(?:\/|$)/i.test(path) || /^api(?:[-.]|$)/i.test(host);
  if (rawFile || apiEndpoint) return url.origin;
  return url.href;
}
